import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Download, Play, Presentation, Video, FileText, ExternalLink,
    Printer, Share2, Copy, CheckCircle, Film, Star, Volume2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Final produced videos
const FINAL_VIDEOS = [
    {
        id: 'advertisement',
        title: 'FendanaCor Advertisement',
        description: '1-minute promotional video with professional voiceover and background music',
        filename: 'FendanaCor_Advertisement.mp4',
        url: '/final_videos/FendanaCor_Advertisement.mp4',
        duration: '16 seconds',
        features: ['Professional voiceover', 'Background music', 'FendanaCor branding'],
        badge: 'FINAL',
        badgeColor: 'bg-emerald-100 text-emerald-700'
    },
    {
        id: 'tutorial',
        title: 'FendanaCor Tutorial',
        description: 'Training video walkthrough with voiceover explaining key features',
        filename: 'FendanaCor_Tutorial.mp4',
        url: '/final_videos/FendanaCor_Tutorial.mp4',
        duration: '16 seconds',
        features: ['Step-by-step guide', 'Voiceover narration', 'FendanaCor branding'],
        badge: 'FINAL',
        badgeColor: 'bg-emerald-100 text-emerald-700'
    }
];

export default function MarketingMaterialsPage() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tutorialPrompts, setTutorialPrompts] = useState(null);
    const [advertPrompts, setAdvertPrompts] = useState(null);

    useEffect(() => {
        fetchMaterials();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMaterials = async () => {
        try {
            const [videosRes, tutorialRes, advertRes] = await Promise.all([
                fetch(`${API}/video/list`).then(r => r.json()),
                fetch(`${API}/video/tutorial-prompts`).then(r => r.json()),
                fetch(`${API}/video/advert-prompts`).then(r => r.json())
            ]);
            setVideos(videosRes.videos || []);
            setTutorialPrompts(tutorialRes);
            setAdvertPrompts(advertRes);
        } catch (error) {
            console.error('Error fetching materials:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const printPresentation = () => {
        window.open('/investor-pitch', '_blank');
        toast.info('Open the presentation and use Ctrl+P to print');
    };

    const downloadVideo = (url, filename) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success(`Downloading ${filename}`);
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="marketing-materials-page">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <img src="/logo.png" alt="FendanaCor" className="w-10 h-10 rounded-lg" />
                            <div>
                                <h1 className="font-semibold text-slate-900">Marketing Materials</h1>
                                <p className="text-xs text-slate-500">Investor pitch, videos & downloads</p>
                            </div>
                        </div>
                        <Button variant="outline" onClick={() => window.location.href = '/dashboard'}>
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
                {/* Investor Pitch Section */}
                <section>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <Presentation className="w-6 h-6 text-teal-600" />
                        Investor Pitch Presentation
                    </h2>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row gap-6 items-start">
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                        FendanaCor Investor Pitch Deck
                                    </h3>
                                    <p className="text-slate-600 mb-4">
                                        12-slide interactive presentation covering the problem, solution, market opportunity, 
                                        cost savings, and investment ask (£2M Seed Round).
                                    </p>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        <Badge className="bg-teal-100 text-teal-700">12 Slides</Badge>
                                        <Badge className="bg-purple-100 text-purple-700">Interactive</Badge>
                                        <Badge className="bg-blue-100 text-blue-700">Animated</Badge>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        <Button onClick={() => window.open('/investor-pitch', '_blank')}>
                                            <Play className="w-4 h-4 mr-2" />
                                            View Presentation
                                        </Button>
                                        <Button variant="outline" onClick={printPresentation}>
                                            <Printer className="w-4 h-4 mr-2" />
                                            Print / Export PDF
                                        </Button>
                                        <Button variant="outline" onClick={() => {
                                            copyToClipboard(window.location.origin + '/investor-pitch');
                                        }}>
                                            <Share2 className="w-4 h-4 mr-2" />
                                            Share Link
                                        </Button>
                                    </div>
                                </div>
                                <div className="w-full md:w-64 aspect-video bg-gradient-to-br from-teal-600 to-emerald-600 rounded-lg flex items-center justify-center text-white">
                                    <div className="text-center">
                                        <Presentation className="w-12 h-12 mx-auto mb-2" />
                                        <span className="font-semibold">Pitch Deck</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </section>

                {/* FINAL PRODUCED VIDEOS - Featured Section */}
                <section>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <Star className="w-6 h-6 text-amber-500" />
                        Final Produced Videos
                        <Badge className="bg-emerald-100 text-emerald-700 ml-2">Ready to Use</Badge>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {FINAL_VIDEOS.map((video) => (
                            <Card key={video.id} className="border-2 border-emerald-200 bg-gradient-to-br from-white to-emerald-50">
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="flex items-center gap-2">
                                            <Film className="w-5 h-5 text-emerald-600" />
                                            {video.title}
                                        </CardTitle>
                                        <Badge className={video.badgeColor}>{video.badge}</Badge>
                                    </div>
                                    <CardDescription>{video.description}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {/* Video Player */}
                                        <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden">
                                            <video 
                                                src={video.url} 
                                                className="w-full h-full"
                                                controls
                                                poster="/logo.png"
                                            />
                                        </div>
                                        
                                        {/* Features */}
                                        <div className="flex flex-wrap gap-2">
                                            {video.features.map((feature, idx) => (
                                                <Badge key={idx} variant="outline" className="text-xs">
                                                    <Volume2 className="w-3 h-3 mr-1" />
                                                    {feature}
                                                </Badge>
                                            ))}
                                        </div>
                                        
                                        {/* Actions */}
                                        <div className="flex gap-2">
                                            <Button 
                                                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                                onClick={() => downloadVideo(video.url, video.filename)}
                                            >
                                                <Download className="w-4 h-4 mr-2" />
                                                Download Video
                                            </Button>
                                            <Button 
                                                variant="outline"
                                                onClick={() => {
                                                    copyToClipboard(window.location.origin + video.url);
                                                }}
                                            >
                                                <Share2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </section>

                {/* Videos Section */}
                <section>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <Video className="w-6 h-6 text-purple-600" />
                        Marketing Videos
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Tutorial Video */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Film className="w-5 h-5 text-teal-600" />
                                        Tutorial Video
                                    </CardTitle>
                                    <Badge>{tutorialPrompts?.total_duration_seconds || 32} seconds</Badge>
                                </div>
                                <CardDescription>
                                    3-minute walkthrough of the FendanaCor system for new users
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center">
                                        <div className="text-center text-slate-400">
                                            <Video className="w-12 h-12 mx-auto mb-2" />
                                            <p className="text-sm">Video ready for production</p>
                                            <p className="text-xs">8 scenes with AI-generated clips</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" className="flex-1" onClick={() => window.open('/tutorial', '_blank')}>
                                            <FileText className="w-4 h-4 mr-2" />
                                            View Script
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Advertisement Video */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Film className="w-5 h-5 text-purple-600" />
                                        Advertisement Video
                                    </CardTitle>
                                    <Badge variant="secondary">{advertPrompts?.total_duration_seconds || 16} seconds</Badge>
                                </div>
                                <CardDescription>
                                    1-minute promotional video for social media and website
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center">
                                        <div className="text-center text-slate-400">
                                            <Video className="w-12 h-12 mx-auto mb-2" />
                                            <p className="text-sm">Video ready for production</p>
                                            <p className="text-xs">4 scenes with AI-generated clips</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" className="flex-1" onClick={() => window.open('/tutorial', '_blank')}>
                                            <FileText className="w-4 h-4 mr-2" />
                                            View Script
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </section>

                {/* Generated Video Clips */}
                {videos.length > 0 && (
                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <Download className="w-6 h-6 text-green-600" />
                            Generated Video Clips
                        </h2>
                        <Card>
                            <CardContent className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {videos.map((video, index) => (
                                        <div key={index} className="border rounded-lg p-3">
                                            <video 
                                                src={video.url} 
                                                className="w-full aspect-video rounded mb-2"
                                                controls
                                            />
                                            <p className="text-sm font-medium truncate">{video.filename}</p>
                                            <p className="text-xs text-slate-500">{video.size_mb} MB</p>
                                            <Button 
                                                variant="outline" 
                                                size="sm" 
                                                className="w-full mt-2"
                                                onClick={() => {
                                                    const a = document.createElement('a');
                                                    a.href = video.url;
                                                    a.download = video.filename;
                                                    a.click();
                                                }}
                                            >
                                                <Download className="w-3 h-3 mr-1" />
                                                Download
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </section>
                )}

                {/* Quick Links */}
                <section>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">Quick Links</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.open('/investor-pitch', '_blank')}>
                            <CardContent className="p-4 text-center">
                                <Presentation className="w-8 h-8 mx-auto mb-2 text-teal-600" />
                                <p className="font-medium">Investor Pitch</p>
                                <p className="text-xs text-slate-500">12 slides</p>
                            </CardContent>
                        </Card>
                        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.open('/tutorial', '_blank')}>
                            <CardContent className="p-4 text-center">
                                <FileText className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                                <p className="font-medium">Tutorial Guide</p>
                                <p className="text-xs text-slate-500">Interactive</p>
                            </CardContent>
                        </Card>
                        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => window.open('/tutorial?tab=scripts', '_blank')}>
                            <CardContent className="p-4 text-center">
                                <Video className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                                <p className="font-medium">Video Scripts</p>
                                <p className="text-xs text-slate-500">Ready to record</p>
                            </CardContent>
                        </Card>
                        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => {
                            copyToClipboard(window.location.origin);
                            toast.success('App URL copied!');
                        }}>
                            <CardContent className="p-4 text-center">
                                <Share2 className="w-8 h-8 mx-auto mb-2 text-green-600" />
                                <p className="font-medium">Share App</p>
                                <p className="text-xs text-slate-500">Copy link</p>
                            </CardContent>
                        </Card>
                    </div>
                </section>
            </main>
        </div>
    );
}
